import { useState } from 'react'
import type { ReactNode } from 'react'
import { AlertOctagon, RotateCcw, ShieldAlert } from 'lucide-react'
import { Button, Card, MoneyValue, SegmentedControl, Skeleton } from '@shared/design-system/ui'
import { useAdminParticipantDashboard, useAdminProductSales } from '../api/admin.api'
import { productSalesRanking, userPerformance } from '../lib/admin-user-performance'
import {
  comparedCurrencies,
  salesComparison,
  volumeComparison,
} from '../lib/admin-user-performance-periods'
import type {
  ProductSalesRankingItem,
  UserPerformance,
  UserPerformanceCurrency,
} from '../lib/admin-user-performance'
import {
  analyticsGrid,
  barTrack,
  barValue,
  description,
  empty,
  heading,
  insightGrid,
  moneyList,
  panel,
  panelHeader,
  panelTitle,
  periodCard,
  periodGrid,
  periodHelper,
  periodLabel,
  periodValue,
  permissionNotice,
  rank,
  rankedIdentity,
  rankedItem,
  rankedList,
  rankedMeta,
  rankedName,
  riskCard,
  riskHeader,
  riskIcon,
  riskLabel,
  riskRate,
  riskRow,
  riskRows,
  section,
  sectionHeader,
  title,
} from './AdminUserPerformance.css'

type Period = '7d' | '30d' | 'all'

const periodOptions = [
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: 'all', label: 'Histórico' },
] as const

export function AdminUserPerformance({
  userId,
  canReadRevenue,
  canReadProducts,
}: {
  userId: string
  canReadRevenue: boolean
  canReadProducts: boolean
}) {
  const [period, setPeriod] = useState<Period>('30d')
  const last7Days = useAdminParticipantDashboard(
    { participantId: userId, period: '7d' },
    canReadRevenue,
  )
  const previous7Days = useAdminParticipantDashboard(
    { participantId: userId, period: '7d', comparison: 'previous' },
    canReadRevenue,
  )
  const last30Days = useAdminParticipantDashboard(
    { participantId: userId, period: '30d' },
    canReadRevenue,
  )
  const previous30Days = useAdminParticipantDashboard(
    { participantId: userId, period: '30d', comparison: 'previous' },
    canReadRevenue,
  )
  const historical = useAdminParticipantDashboard(
    { participantId: userId, period: 'all' },
    canReadRevenue,
  )
  const productSales = useAdminProductSales(
    { participantId: userId, period },
    canReadRevenue && canReadProducts,
  )
  const periodQuery = {
    '7d': last7Days,
    '30d': last30Days,
    all: historical,
  }[period]

  if (!canReadRevenue) {
    return (
      <div className={permissionNotice}>
        Seu perfil pode consultar o cadastro, mas não possui a permissão de receita necessária para
        ver as métricas deste usuário.
      </div>
    )
  }

  const selected = userPerformance(periodQuery.data)
  const productRanking = productSalesRanking(productSales.data?.items)

  return (
    <section className={section} aria-labelledby="user-performance-title">
      <div className={sectionHeader}>
        <div className={heading}>
          <h2 className={title} id="user-performance-title">
            Desempenho e risco
          </h2>
          <p className={description}>
            Vendas pelo período de criação. Reembolsos e chargebacks usam todas as vendas concluídas
            como base e nunca somam moedas diferentes.
          </p>
        </div>
        <SegmentedControl
          value={period}
          options={periodOptions}
          ariaLabel="Período das métricas do usuário"
          onChange={setPeriod}
        />
      </div>

      <div className={periodGrid}>
        <PeriodSnapshot
          label="Últimos 7 dias"
          query={last7Days}
          previousQuery={previous7Days}
          comparisonLabel="7 dias anteriores"
        />
        <PeriodSnapshot
          label="Últimos 30 dias"
          query={last30Days}
          previousQuery={previous30Days}
          comparisonLabel="30 dias anteriores"
        />
        <PeriodSnapshot label="Todo o histórico" query={historical} />
      </div>

      {periodQuery.isLoading ? (
        <Skeleton height={260} />
      ) : periodQuery.isError ? (
        <Card padding="md">
          <div className={panel}>
            <span className={empty}>Não foi possível carregar os indicadores deste período.</span>
            <Button variant="secondary" onClick={() => void periodQuery.refetch()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <div className={analyticsGrid}>
            <RiskCard
              label="Taxa de chargeback"
              icon={<AlertOctagon size={18} />}
              count={selected.chargebackCount}
              countRate={selected.chargebackCountRate}
              processedCount={selected.processedCount}
              currencies={selected.currencies}
              amountKey="chargedBackAmount"
              amountRateKey="chargebackAmountRate"
            />
            <RiskCard
              label="Taxa de reembolso"
              icon={<RotateCcw size={18} />}
              count={selected.refundCount}
              countRate={selected.refundCountRate}
              processedCount={selected.processedCount}
              currencies={selected.currencies}
              amountKey="refundedAmount"
              amountRateKey="refundAmountRate"
              helper="Inclui os reembolsos tratados como proteção de chargeback."
            />
            <RiskCard
              label="Reembolso de chargeback"
              icon={<ShieldAlert size={18} />}
              count={selected.chargebackRefundCount}
              countRate={selected.chargebackRefundCountRate}
              processedCount={selected.processedCount}
              currencies={selected.currencies}
              amountKey="chargebackRefundAmount"
              amountRateKey="chargebackRefundAmountRate"
              helper="É um subconjunto dos reembolsos, destacado para medir a proteção global."
            />
          </div>

          <div className={insightGrid}>
            <Card padding="md" className={panel}>
              <div className={panelHeader}>
                <h3 className={panelTitle}>Principais moedas</h3>
                <p className={description}>
                  Ordenadas por número de vendas, com o volume separado.
                </p>
              </div>
              <CurrencyRanking performance={selected} />
            </Card>

            <Card padding="md" className={panel}>
              <div className={panelHeader}>
                <h3 className={panelTitle}>Produtos mais vendidos</h3>
                <p className={description}>
                  Ranking por quantidade de vendas concluídas no período selecionado.
                </p>
              </div>
              {!canReadProducts ? (
                <span className={empty}>Seu perfil não pode consultar produtos.</span>
              ) : productSales.isLoading || productSales.isFetching ? (
                <Skeleton height={170} />
              ) : productSales.isError ? (
                <span className={empty}>Não foi possível montar o ranking de produtos.</span>
              ) : productRanking.length === 0 ? (
                <span className={empty}>Nenhum produto com venda concluída neste período.</span>
              ) : (
                <div className={rankedList}>
                  {productRanking.map((item, index) => (
                    <ProductRankingItem key={item.productId} index={index} product={item} />
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </section>
  )
}

function PeriodSnapshot({
  label,
  query,
  previousQuery,
  comparisonLabel,
}: {
  label: string
  query: ReturnType<typeof useAdminParticipantDashboard>
  previousQuery?: ReturnType<typeof useAdminParticipantDashboard>
  comparisonLabel?: string
}) {
  const performance = userPerformance(query.data)
  const previousPerformance = userPerformance(previousQuery?.data)
  const currencies = comparedCurrencies(performance.currencies, previousPerformance.currencies)
  return (
    <Card padding="md" className={periodCard}>
      <span className={periodLabel}>{label}</span>
      {query.isLoading ? (
        <Skeleton height={72} />
      ) : query.isError ? (
        <span className={empty}>Indicador indisponível.</span>
      ) : (
        <>
          <strong className={periodValue}>{performance.processedCount}</strong>
          <span className={periodHelper}>
            {performance.processedCount === 1 ? 'venda concluída' : 'vendas concluídas'} · pagas,
            reembolsadas ou com chargeback
          </span>
          {previousQuery && comparisonLabel ? (
            previousQuery.isLoading ? (
              <Skeleton height={18} />
            ) : previousQuery.isError ? (
              <span className={periodHelper}>Comparação anterior indisponível.</span>
            ) : (
              <span className={periodHelper}>
                {salesComparison(
                  performance.processedCount,
                  previousPerformance.processedCount,
                  comparisonLabel,
                )}
              </span>
            )
          ) : null}
          <div className={moneyList}>
            {currencies.map((currency) => {
              return (
                <div key={currency.currency}>
                  <MoneyValue
                    amount={currency.currentAmount}
                    currency={currency.currency}
                    size="sm"
                  />
                  {previousQuery &&
                  comparisonLabel &&
                  !previousQuery.isLoading &&
                  !previousQuery.isError ? (
                    <span className={periodHelper}>
                      {volumeComparison(
                        currency.currentAmount,
                        currency.previousAmount,
                        comparisonLabel,
                      )}
                    </span>
                  ) : null}
                </div>
              )
            })}
          </div>
        </>
      )}
    </Card>
  )
}

type AmountKey = 'chargedBackAmount' | 'refundedAmount' | 'chargebackRefundAmount'
type AmountRateKey = 'chargebackAmountRate' | 'refundAmountRate' | 'chargebackRefundAmountRate'

function RiskCard({
  label,
  icon,
  count,
  countRate,
  processedCount,
  currencies,
  amountKey,
  amountRateKey,
  helper,
}: {
  label: string
  icon: ReactNode
  count: number
  countRate: number
  processedCount: number
  currencies: UserPerformanceCurrency[]
  amountKey: AmountKey
  amountRateKey: AmountRateKey
  helper?: string
}) {
  const affectedCurrencies = currencies.filter((currency) => Number(currency[amountKey]) > 0)
  return (
    <Card padding="md" className={riskCard}>
      <div className={riskHeader}>
        <span className={riskLabel}>{label}</span>
        <span className={riskIcon}>{icon}</span>
      </div>
      <strong className={riskRate}>{formatRate(countRate)}</strong>
      <span className={periodHelper}>
        {count} de {processedCount} {processedCount === 1 ? 'venda concluída' : 'vendas concluídas'}
      </span>
      {helper ? <span className={periodHelper}>{helper}</span> : null}
      <div className={riskRows}>
        {affectedCurrencies.length > 0 ? (
          affectedCurrencies.map((currency) => (
            <div className={riskRow} key={currency.currency}>
              <span>
                {currency.currency} · {formatRate(currency[amountRateKey])} do volume
              </span>
              <MoneyValue amount={currency[amountKey]} currency={currency.currency} size="sm" />
            </div>
          ))
        ) : (
          <span className={periodHelper}>Nenhum valor afetado no período.</span>
        )}
      </div>
    </Card>
  )
}

function CurrencyRanking({ performance }: { performance: UserPerformance }) {
  if (performance.currencies.length === 0) {
    return <span className={empty}>Nenhuma venda concluída neste período.</span>
  }
  return (
    <div className={rankedList}>
      {performance.currencies.map((currency, index) => {
        const share =
          performance.processedCount > 0
            ? (currency.salesCount / performance.processedCount) * 100
            : 0
        return (
          <div className={rankedItem} key={currency.currency}>
            <span className={rank}>{index + 1}</span>
            <div className={rankedIdentity}>
              <span className={rankedName}>{currency.currency}</span>
              <span className={rankedMeta}>
                {currency.salesCount} {currency.salesCount === 1 ? 'venda' : 'vendas'} ·{' '}
                {formatRate(share)} do total
              </span>
              <span className={barTrack} aria-hidden="true">
                <span className={barValue} style={{ width: `${share}%` }} />
              </span>
            </div>
            <MoneyValue amount={currency.processedAmount} currency={currency.currency} size="sm" />
          </div>
        )
      })}
    </div>
  )
}

function ProductRankingItem({
  index,
  product,
}: {
  index: number
  product: ProductSalesRankingItem
}) {
  return (
    <div className={rankedItem}>
      <span className={rank}>{index + 1}</span>
      <div className={rankedIdentity}>
        <span className={rankedName}>{product.productName}</span>
        <span className={rankedMeta}>
          {product.processedCount}{' '}
          {product.processedCount === 1 ? 'venda concluída' : 'vendas concluídas'}
        </span>
      </div>
      <div className={moneyList}>
        {product.currencies.map((currency) => (
          <MoneyValue
            key={currency.currency}
            amount={currency.amount}
            currency={currency.currency}
            size="sm"
          />
        ))}
      </div>
    </div>
  )
}

function formatRate(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: value > 0 && value < 0.1 ? 2 : 1,
    maximumFractionDigits: 2,
  }).format(value / 100)
}
